import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import TableContainer from '../../components/Common/DataTableContainer';

import axios from 'axios';
import moment from 'moment';

const BookingsHistory = ({ customerId }) => {
    const navigate = useNavigate();
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (customerId) {
            fetchBookings();
        }
    }, [customerId]);

    const fetchBookings = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_WEB_APP_URL}/api/web/bookings`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                }
            );
            
            if (response.data.success) {
                const allBookings = response.data.data || [];
                // Filter bookings by customer ID
                const filteredBookings = allBookings.filter(item => {
                    const booking = item.booking;
                    const customer = item.customer;
                    if (!booking) return false;
                    
                    return customer?.id === parseInt(customerId) || 
                           customer?.id === customerId ||
                           booking.customer_id === parseInt(customerId) ||
                           booking.customer_id === customerId;
                });
                setBookings(filteredBookings);
            }
        } catch (error) {
            console.error("Error fetching bookings:", error);
        } finally {
            setLoading(false);
        }
    };

    const columns = useMemo(
        () => [
            {
                header: 'Booking Id',
                accessorKey: 'bookingId',
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: 'Category Type',
                accessorKey: 'categoryType',
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: 'Vehicle Type',
                accessorKey: 'vehicleType',
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: 'Payment Status',
                accessorKey: 'payment_status',
                enableColumnFilter: false,
                enableSorting: true,
                cell: ({ row }) => {
                    const status = row?.original?.payment_status?.toLowerCase();

                    let badgeClass = "badge-secondary";
                    if (status === "paid") badgeClass = "badge-success";
                    else if (status === "pending") badgeClass = "badge-warning";

                    return (
                        <span className={`badge ${badgeClass}`}>
                            {row?.original?.payment_status?.toUpperCase() || "N/A"}
                        </span>
                    );
                }
            },
            {
                header: 'Status',
                accessorKey: 'status',
                enableColumnFilter: false,
                enableSorting: true,
                cell: ({ row }) => {
                    const status = row?.original?.status?.toLowerCase();

                    let badgeClass = "badge-secondary";
                    let Status = "Pending";

                    switch (status) {
                        case "pending":
                            badgeClass = "badge-secondary";
                            Status = "Pending";
                            break;
                        case "ongoing":
                            badgeClass = "badge-primary";
                            Status = "Ongoing";
                            break;
                        case "arrived":
                            badgeClass = "badge-info";
                            Status = "Arrived";
                            break;
                        case "start":
                            badgeClass = "badge-dark";
                            Status = "Started";
                            break;
                        case "complete":
                            badgeClass = "badge-success";
                            Status = "Completed";
                            break;
                        case "cancel":
                            badgeClass = "badge-danger";
                            Status = "Cancelled";
                            break;
                        case "expired":
                            badgeClass = "badge-warning";
                            Status = "Expired";
                            break;
                        case "scheduled":
                            badgeClass = "badge-primary";
                            Status = "Scheduled";
                            break;
                        case "scheduled_accept":
                            badgeClass = "badge-info";
                            Status = "Scheduled Accepted";
                            break;
                        case "hooked":
                            badgeClass = "badge-dark";
                            Status = "Hooked";
                            break;
                        case "loading_proccess":
                            badgeClass = "badge-warning";
                            Status = "Loading Process";
                            break;
                        case "unloading_proccess":
                            badgeClass = "badge-warning";
                            Status = "Unloading Process";
                            break;
                        default:
                            badgeClass = "badge-secondary";
                            Status = status ? status.charAt(0).toUpperCase() + status.slice(1) : "Pending";
                    }

                    return (
                        <span className={`badge ${badgeClass}`}>
                            {Status}
                        </span>
                    );
                }
            },
            {
                header: 'Amount',
                accessorKey: 'amount',
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: 'Created At',
                accessorKey: 'createdAt',
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: 'Action',
                id: 'action',
                cell: ({ row }) => {  
                    const bookingId = row.original?.bookingId;
                    return (
                        <button
                            className="btn btn-sm btn-success"
                            onClick={() => navigate(`/bookings/${bookingId}`)}
                        >
                            <i className="mdi mdi-eye"></i> View
                        </button>
                    );
                }
            }
        ],
        [navigate]
    );

    const transformedData = useMemo(() => {
        if (!bookings || bookings.length === 0) {
            return [];
        }
        
        return bookings
            .filter(item => item.booking)
            .map(item => {
                return {
                    bookingId: item.booking?.id || "N/A",
                    categoryType: item?.booking?.type === "taxi" ? 'Ride Hailing' : (item?.booking?.type || "N/A"),
                    vehicleType: item.vehicle_type?.type_name || "Type supprimé",
                    payment_status: item.booking?.payment_status || "N/A",
                    status: item.booking?.status || "N/A",
                    amount: item.booking?.total_amount || 0.00,
                    createdAt: moment(item?.booking?.created_at).format("DD-MM-YY hh:mm A") || "N/A",
                };
            });
    }, [bookings]);

    if (loading) {
        return <Skeleton count={10} height={40} />;
    }

    const totalRecords = transformedData?.length ?? 0;

    return (
        <TableContainer
            columns={columns}
            data={transformedData || []}
            isGlobalFilter={true}
            isPagination={true}
            isCustomPageSize={true}
            SearchPlaceholder={`Looking for something? ${totalRecords} results loaded.`}
            pagination="pagination"
            paginationWrapper='dataTables_paginate paging_simple_numbers'
            tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />
    );
};

BookingsHistory.propTypes = {
    customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default BookingsHistory;

